import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { parseCertificateMetadata } from "./certificate-utils";
import type { CertificateAward } from "./certificate-utils";

// Static `new URL(..., import.meta.url)` resolves under both Vite (client)
// and Next (CMS); keep each path a literal so both bundlers can see it.
const NUNITO_BOLD = new URL("./assets/Nunito-Bold.ttf", import.meta.url).href;
const MULISH_MEDIUM = new URL("./assets/Mulish-Medium.ttf", import.meta.url)
  .href;
const MULISH_BOLD = new URL("./assets/Mulish-Bold.ttf", import.meta.url).href;
const CORNER_GRAPHIC = new URL(
  "./assets/syep-certificate-corner-graphic.png",
  import.meta.url,
).href;
const STARLIGHT_TWINKLE = new URL(
  "./assets/starlight-twinkle.png",
  import.meta.url,
).href;

// Font.register is global by family name, so these names are certificate-only:
// the client's other PDFs register their own "Nunito"/"Mulish" families.
let fontsRegistered = false;
export const registerCertificateFonts = () => {
  if (fontsRegistered) return;
  fontsRegistered = true;
  Font.register({
    family: "CertificateNunito",
    src: NUNITO_BOLD,
    fontWeight: "bold",
  });
  Font.register({
    family: "CertificateMulish",
    src: MULISH_MEDIUM,
    fontWeight: 400,
  });
  Font.register({
    family: "CertificateMulishBold",
    src: MULISH_BOLD,
    fontWeight: 700,
  });
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#fff",
  },
  cornerImage: {
    position: "absolute",
    right: -25,
    top: -25,
    width: 190,
    height: 190,
  },
  container: {
    flex: 1,
    backgroundColor: "#fff",
    padding: 20,
  },
  body: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  logo: {
    marginBottom: 20,
  },
  header: {
    flexDirection: "row",
  },
  title: {
    fontSize: 18,
    marginBottom: 20,
    marginTop: 20,
    fontFamily: "CertificateMulish",
  },
  name: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 20,
    color: "#8420B4",
    fontFamily: "CertificateNunito",
  },
  text: {
    fontSize: 14,
    marginBottom: 5,
    fontFamily: "CertificateMulish",
  },
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    maxWidth: 460,
  },
  skillTag: {
    backgroundColor: "#E2B6FF",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    fontSize: 10,
    color: "#8420B4",
    fontFamily: "CertificateMulishBold",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    marginTop: 20,
    fontSize: 12,
  },
  inline: {
    flexDirection: "row",
    width: 380,
    marginRight: 55,
  },
  footerTextTop: {
    minWidth: 135,
    marginRight: 35,
    fontFamily: "CertificateMulish",
    fontSize: 16,
  },
  footerText: {
    minWidth: 135,
    marginRight: 35,
    fontFamily: "CertificateMulish",
    fontSize: 12,
  },
  footerBar: {
    minWidth: 135,
    height: 2,
    marginRight: 35,
    backgroundColor: "black",
    marginTop: 10,
    marginBottom: 10,
  },
});

export interface CertificatePDFProps {
  award: CertificateAward;
  // Already formatted for display, e.g. via formatCompletionDate.
  completionDate: string;
  recipientName: string;
}

export const CertificatePDF = ({
  award,
  completionDate,
  recipientName,
}: CertificatePDFProps) => {
  registerCertificateFonts();
  const config = award.certificateConfig || {};
  const metadata = parseCertificateMetadata(config.metadata);
  const backgroundColor = metadata.backgroundColor || "#FFD100";
  const estimatedCertTime = metadata.estimatedTrainingHours;
  const signatories = metadata.signatories || [];
  const skills = (config.skills || []).map((s) => s.skill);
  const logoUrl = config.logoImage?.url;
  const badgeUrl = config.badgeImage?.url || award.image?.url;

  return (
    <Document>
      <Page size="LETTER" orientation="landscape" style={styles.page}>
        <View style={{ flex: 1, padding: 25, backgroundColor }}>
          <View style={styles.container}>
            <Image src={CORNER_GRAPHIC} style={styles.cornerImage} />

            {logoUrl && (
              <View style={styles.logo}>
                <Image src={logoUrl} style={{ maxWidth: 240, maxHeight: 77 }} />
              </View>
            )}

            <View style={styles.header}>
              <Text style={styles.title}>THIS CERTIFICATE IS AWARDED TO</Text>
              <Image
                src={STARLIGHT_TWINKLE}
                style={{ width: "36px", height: "36px", marginTop: -10 }}
              />
            </View>

            <View>
              <Text style={styles.name}>{recipientName}</Text>
              {config.descriptionText && (
                <Text style={[styles.text, { marginBottom: 20 }]}>
                  {config.descriptionText}
                </Text>
              )}
            </View>

            {skills.length > 0 && (
              <View style={styles.body}>
                <View style={styles.skillsContainer}>
                  {skills.map((skill, index) => (
                    <Text key={index} style={styles.skillTag}>
                      {skill}
                    </Text>
                  ))}
                </View>
              </View>
            )}

            <View style={styles.footer}>
              <View style={styles.inline}>
                <Text style={styles.footerTextTop}>{completionDate}</Text>
                {estimatedCertTime && (
                  <Text style={styles.footerTextTop}>
                    {estimatedCertTime} hours
                  </Text>
                )}
                {signatories.map((sig, i) => (
                  <Text key={i} style={styles.footerTextTop}>
                    {sig.name}
                  </Text>
                ))}
              </View>
              <View style={styles.inline}>
                <View style={styles.footerBar} />
                {estimatedCertTime && <View style={styles.footerBar} />}
                {signatories.map((_, i) => (
                  <View key={i} style={styles.footerBar} />
                ))}
              </View>
              <View style={styles.inline}>
                <Text style={styles.footerText}>Date Awarded</Text>
                {estimatedCertTime && (
                  <Text style={styles.footerText}>Training Length</Text>
                )}
                {signatories.map((sig, i) => (
                  <Text key={i} style={styles.footerText}>
                    {sig.title}
                  </Text>
                ))}
              </View>
            </View>
          </View>

          {badgeUrl && (
            <Image
              src={badgeUrl}
              style={{
                width: 260,
                height: 260,
                padding: 20,
                position: "absolute",
                bottom: 20,
                right: 20,
              }}
            />
          )}
        </View>
      </Page>
    </Document>
  );
};
